import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Products.css';
import './ProductsActions.css';
import './MobileResponsive.css';
import apiService from '../../services/api';
import AddPurchase from './AddPurchase';
import AddPurchaseReturn from './AddPurchaseReturn';
import AddPurchaseRequisition from './AddPurchaseRequisition';
import { exportToCSV, exportToExcel, exportToPDF, printPage } from './exportUtils';
import LabelGenerator from './LabelGenerator';
import AddPaymentModal from './AddPaymentModal';
import ViewPaymentsModal from './ViewPaymentsModal';
import UpdatePurchaseStatusModal from './UpdatePurchaseStatusModal';
import PurchaseNotificationModal from './PurchaseNotificationModal';

const Purchases = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('list_purchases');

  // Shared State
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);

  // List Purchases State
  const [purchases, setPurchases] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(25);
  const [totalPurchases, setTotalPurchases] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [openActionId, setOpenActionId] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [loggedInUser, setLoggedInUser] = useState(null);
  const [showLabelModal, setShowLabelModal] = useState(false);
  const [productsForLabels, setProductsForLabels] = useState([]);
  const [showAddPaymentModal, setShowAddPaymentModal] = useState(false);
  const [showViewPaymentsModal, setShowViewPaymentsModal] = useState(false);
  const [showUpdateStatusModal, setShowUpdateStatusModal] = useState(false);
  const [showNotificationModal, setShowNotificationModal] = useState(false);

  // Fetch Purchases
  useEffect(() => {
    if (activeTab === 'list_purchases') {
      fetchPurchases();
    }
  }, [activeTab, currentPage, perPage, searchTerm]);

  const fetchPurchases = async () => {
    setIsLoading(true);
    try {
      console.log('Fetching purchases...');
      
      let locId = null;
      let bizId = null;
      
      try {
        const userDetails = await apiService.getUserDetails();
        const userData = userDetails.data || userDetails;
        setLoggedInUser(userData);
        locId = userData.location_id;
        bizId = userData.business_id;
      } catch (err) {
        console.warn('Could not fetch user details for filtering:', err);
      }

      const formatForApi = (date) => {
        if (!date) return '';
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      };

      const now = new Date();
      const startDateDefault = new Date();
      startDateDefault.setFullYear(now.getFullYear() - 2); // 2 years ago

      const params = { 
        page: currentPage,
        per_page: 100, // Fetch more records now that it's working
        start_date: formatForApi(startDateDefault),
        end_date: formatForApi(now),
        search: searchTerm 
      };
      
      if (locId) params.location_id = locId;
      if (bizId) params.business_id = bizId;

      console.log('Final request params:', params);
      const data = await apiService.getPurchases(params);
      console.log('Purchases API response:', data);
      
      // Robust mapping to handle various API response formats (Success wrapper, Laravel pagination, etc.)
      let purchaseList = [];
      if (data.success && data.data) {
        purchaseList = Array.isArray(data.data) ? data.data : (data.data.data || []);
      } else {
        purchaseList = Array.isArray(data) ? data : (data.data || []);
        // Check for double nesting common in paginated results
        if (!Array.isArray(purchaseList) && purchaseList.data) {
          purchaseList = purchaseList.data;
        }
      }
      
      console.log('Mapped purchase list:', purchaseList);
      setPurchases(Array.isArray(purchaseList) ? purchaseList : []);
      
      // API returns { current_page, data: [...], last_page, total, ... }
      // Pagination info is at the TOP level of the response, not inside data.data
      if (data.last_page) {
        setLastPage(data.last_page);
        setTotalPurchases(data.total);
      } else if (data.meta?.last_page) {
        setLastPage(data.meta.last_page);
        setTotalPurchases(data.meta.total);
      }
    } catch (err) {
      console.error('Failed to load purchases', err);
      setPurchases([]);
    } finally {
      setIsLoading(false);
    }
  };

  const resolveAddedBy = (purchase) => {
    // 1. Try created_by_user object (Relation)
    if (purchase.created_by_user) {
      return `${purchase.created_by_user.first_name || ''} ${purchase.created_by_user.last_name || ''}`.trim() || purchase.created_by_user.username;
    }
    
    // 2. Try created_by object (Flattened or alternate name)
    if (typeof purchase.created_by === 'object' && purchase.created_by) {
      return `${purchase.created_by.first_name || ''} ${purchase.created_by.last_name || ''}`.trim() || purchase.created_by.username;
    }

    // 3. Session Fallback: If IDs match
    if (loggedInUser && (purchase.created_by === loggedInUser.id || purchase.created_by_id === loggedInUser.id)) {
      return `${loggedInUser.first_name || ''} ${loggedInUser.last_name || ''}`.trim() || loggedInUser.username;
    }

    // 4. Other fields
    return purchase.added_by_name || purchase.created_by_name || 'N/A';
  };

  const handleExport = (format) => {
    if (!purchases || purchases.length === 0) {
      alert('No data to export');
      setShowExportDropdown(false);
      return;
    }

    const headers = ['Action', 'Date', 'Reference No', 'Location', 'Supplier', 'Purchase Status', 'Payment Status', 'Grand Total', 'Payment Due', 'Added By'];
    const rows = purchases.map(p => [
      '', // Action placeholder for export
      p.transaction_date || 'N/A',
      p.ref_no || 'N/A',
      p.location?.name || 'N/A',
      p.supplier?.name || p.contact?.name || 'N/A',
      p.status || 'Pending',
      p.payment_status || 'Due',
      parseFloat(p.final_total || 0).toFixed(2),
      `P: ${parseFloat(p.payment_due || 0).toFixed(2)}${p.return_transaction ? ` / R: ${parseFloat(p.return_transaction.amount_due || 0).toFixed(2)}` : ''}`,
      resolveAddedBy(p)
    ]);

    const title = 'Purchases List';
    const filename = 'purchases';

    if (format === 'CSV') exportToCSV(headers, rows, filename);
    else if (format === 'Excel') exportToExcel(headers, rows, filename);
    else if (format === 'PDF') exportToPDF(title, headers, rows, filename);
    else if (format === 'Print') printPage();

    setShowExportDropdown(false);
  };

  const handlePrint = () => { printPage(); setShowExportDropdown(false); };
  const handleColumnVisibility = () => { alert('Column visibility settings'); setShowExportDropdown(false); };

  const handleAdd = () => {
    if (activeTab === 'list_purchases') {
      setSelectedPurchase(null);
      setActiveTab('add_purchase');
    }
    else if (activeTab === 'purchase_order') navigate('/purchases/order/add');
    else if (activeTab === 'purchase_requisition') setActiveTab('add_purchase_requisition');
    else if (activeTab === 'purchase_return') setActiveTab('add_purchase_return');
  };

  const handleOpenLabels = (purchase) => {
    if (!purchase.purchase_lines || purchase.purchase_lines.length === 0) {
      alert('No products found in this purchase to generate labels.');
      return;
    }

    const mappedProducts = purchase.purchase_lines.map(line => ({
      id: line.product_id,
      name: line.product?.name || `Product #${line.product_id}`,
      sku: line.product?.sku || line.variations?.sub_sku || '--',
      price: parseFloat(line.variations?.sell_price_inc_tax || 0),
      priceExcTax: parseFloat(line.variations?.default_sell_price || 0),
      variation: line.variations?.name || 'Default',
      numberOfLabels: Math.ceil(parseFloat(line.quantity || 1)),
      barcode: line.product?.sku || line.variations?.sub_sku || ''
    }));

    setProductsForLabels(mappedProducts);
    setShowLabelModal(true);
    setOpenActionId(null);
  };

  const handleView = async (purchase) => {
    setOpenActionId(null);
    try {
      setIsLoading(true);
      const response = await apiService.getPurchase(purchase.id);
      let fullPurchase = response.data || response;
      if (Array.isArray(fullPurchase)) fullPurchase = fullPurchase[0];
      setSelectedPurchase(fullPurchase);
    } catch (err) {
      console.warn('Could not fetch full purchase for view, using list data:', err);
      setSelectedPurchase(purchase);  // fallback to list row
    } finally {
      setIsLoading(false);
      setShowViewModal(true);
    }
  };

  const handleEdit = async (purchase) => {
    try {
      setIsLoading(true);
      // Fetch fresh details for this specific purchase to ensure full relation data
      const response = await apiService.getPurchase(purchase.id);
      console.log('[handleEdit] Raw getPurchase response for id', purchase.id, ':', response);
      console.log('[handleEdit] response.data:', response.data);
      console.log('[handleEdit] response.data?.purchase_lines:', response.data?.purchase_lines);

      // API returns { data: { id, purchase_lines, ... } }  — unwrap .data first
      let fullPurchase = response.data || response;

      // Also handle array wrapper e.g. [ { id, purchase_lines, ... } ]
      if (Array.isArray(fullPurchase)) {
        fullPurchase = fullPurchase[0];
      }

      console.log('[handleEdit] fullPurchase being set as editData:', fullPurchase);
      console.log('[handleEdit] purchase_lines count:', fullPurchase?.purchase_lines?.length);
      
      setSelectedPurchase(fullPurchase);
      setActiveTab('add_purchase');
    } catch (err) {
      console.warn('Error fetching full purchase details:', err);
      // Fallback to the current purchase object if individual fetch fails
      setSelectedPurchase(purchase);
      setActiveTab('add_purchase');
    } finally {
      setIsLoading(false);
      setOpenActionId(null);
    }
  };

  const handleAddPayment = (purchase) => {
    setSelectedPurchase(purchase);
    setShowAddPaymentModal(true);
    setOpenActionId(null);
  };

  const handleViewPayments = (purchase) => {
    setSelectedPurchase(purchase);
    setShowViewPaymentsModal(true);
    setOpenActionId(null);
  };

  const handleUpdateStatus = (purchase) => {
    setSelectedPurchase(purchase);
    setShowUpdateStatusModal(true);
    setOpenActionId(null);
  };

  const handleShowNotification = (purchase) => {
    setSelectedPurchase(purchase);
    setShowNotificationModal(true);
    setOpenActionId(null);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this purchase?')) {
      try {
        await apiService.deletePurchase(id);
        alert('Purchase deleted successfully');
        fetchPurchases();
      } catch (err) {
        alert('Failed to delete purchase: ' + err.message);
      }
    }
    setOpenActionId(null);
  };

  if (activeTab === 'add_purchase') {
    return (
      <div className="products-container">
        <AddPurchase 
          onBack={() => {
            setActiveTab('list_purchases');
            setSelectedPurchase(null);
            fetchPurchases(); // Correct function name to refresh list
          }} 
          editData={selectedPurchase}
        />
      </div>
    );
  }

  if (activeTab === 'add_purchase_return') {
    return (
      <div className="products-container">
        <AddPurchaseReturn onBack={() => setActiveTab('purchase_return')} />
      </div>
    );
  }

  if (activeTab === 'add_purchase_requisition') {
    return (
      <div className="products-container">
        <AddPurchaseRequisition onBack={() => setActiveTab('purchase_requisition')} />
      </div>
    );
  }

  return (
    <div className="products-container">
      <div className="main-card">
        {/* Tabs & Search Header */}
        <div className="product-tabs">
          <div
            className={`product-tab ${activeTab === 'list_purchases' ? 'active' : ''}`}
            onClick={() => setActiveTab('list_purchases')}
          >
            <i className="fa fa-list"></i> List Purchases
          </div>
          <div
            className={`product-tab ${activeTab === 'purchase_order' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchase_order')}
          >
            <i className="fa fa-shopping-cart"></i> Purchase Order
          </div>
          <div
            className={`product-tab ${activeTab === 'purchase_requisition' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchase_requisition')}
          >
            <i className="fa fa-file-text"></i> Purchase Requisition
          </div>
          <div
            className={`product-tab ${activeTab === 'purchase_return' ? 'active' : ''}`}
            onClick={() => setActiveTab('purchase_return')}
          >
            <i className="fa fa-undo"></i> List Purchase Return
          </div>
        </div>

        {/* Table Controls */}
        <div className="table-controls">
          <div className="table-controls-left">
            <div className="entries-dropdown">
              <span>Show</span>
              <select value={perPage} onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
              <span>entries</span>
            </div>
          </div>
          <div className="table-controls-right">
            <div className="tabs-search-wrapper">
              <input
                type="text"
                placeholder="Search..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="tabs-search-input"
              />
              <button className="filter-toggle-btn" onClick={() => setShowFilters(true)} title="Open Filters">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" />
                </svg>
                Filters
              </button>
            </div>
            <div className="export-dropdown-container" style={{ position: 'relative' }}>
              <button className="export-btn" onClick={() => setShowExportDropdown(!showExportDropdown)}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                  <polyline points="7 10 12 15 17 10"></polyline>
                  <line x1="12" y1="15" x2="12" y2="3"></line>
                </svg>
                Export ▾
              </button>
              {showExportDropdown && (
                <div className="export-dropdown-menu">
                  <button onClick={() => handleExport('CSV')}><i className="fa fa-file-csv"></i> Export CSV</button>
                  <button onClick={() => handleExport('Excel')}><i className="fa fa-file-excel"></i> Export Excel</button>
                  <button onClick={() => handleExport('Print')}><i className="fa fa-print"></i> Print</button>
                  <button onClick={() => handleExport('PDF')}><i className="fa fa-file-pdf"></i> Export PDF</button>
                </div>
              )}
            </div>
            <button className="control-btn-primary" onClick={handleAdd}>
              <span className="plus-icon">+</span> Add
            </button>
          </div>
        </div>

        {/* Table Content */}
        <div className="table-content-card">
          {activeTab === 'list_purchases' && (
            isLoading ? (
              <div style={{ padding: '3rem', textAlign: 'center', color: '#64748b' }}>
                <div style={{ marginBottom: '1rem', fontSize: '1.2rem', fontWeight: '500' }}>⏳ Loading Purchases...</div>
              </div>
            ) : (
              <>
                <div className="products-table-container">
                  <table className="products-table">
                    <thead>
                      <tr>
                        <th>Action</th>
                        <th>Date</th>
                        <th>Reference No</th>
                        <th>Location</th>
                        <th>Supplier</th>
                        <th>Purchase Status</th>
                        <th>Payment Status</th>
                        <th>Grand Total</th>
                        <th>Payment Due</th>
                        <th>Added By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchases.length === 0 ? (
                        <tr><td colSpan="10" style={{ textAlign: 'center', padding: '2rem' }}>No purchases found.</td></tr>
                      ) : (
                        purchases.map(purchase => (
                          <tr key={purchase.id}>
                            <td className="actions-cell">
                              <div className="action-dropdown-container">
                                <button className="action-btn-trigger" onClick={() => setOpenActionId(openActionId === purchase.id ? null : purchase.id)}>
                                  Actions <span className="arrow-down">▼</span>
                                </button>
                                {openActionId === purchase.id && (
                                  <div className="action-dropdown-menu">
                                    <button onClick={() => handleView(purchase)}><i className="fa fa-eye"></i> View</button>
                                    <button onClick={() => handlePrint(purchase)}><i className="fa fa-print"></i> Print</button>
                                    <button onClick={() => handleEdit(purchase)}><i className="fa fa-edit"></i> Edit</button>
                                    <button onClick={() => handleDelete(purchase.id)} className="delete-btn"><i className="fa fa-trash"></i> Delete</button>
                                    <button onClick={() => handleOpenLabels(purchase)}><i className="fa fa-barcode"></i> Labels</button>
                                    
                                    <div className="divider"></div>
                                    
                                    {purchase.payment_status?.toLowerCase() !== 'paid' && (
                                      <button onClick={() => handleAddPayment(purchase)}>
                                        <i className="fa fa-money-bill-wave"></i> Add Payment
                                      </button>
                                    )}
                                    <button onClick={() => handleViewPayments(purchase)}>
                                      <i className="fa fa-money"></i> View Payments
                                    </button>
                                    <button onClick={() => handleUpdateStatus(purchase)}><i className="fa fa-check-square-o"></i> Update Status</button>
                                    <button onClick={() => handleShowNotification(purchase)}><i className="fa fa-envelope"></i> Items Pending Notification</button>
                                    <button onClick={() => alert('Purchase Return functionality coming soon!')}><i className="fa fa-undo"></i> Purchase Return</button>
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>{purchase.transaction_date || 'N/A'}</td>
                            <td>{purchase.ref_no || 'N/A'}</td>
                            <td>{purchase.location?.name || 'N/A'}</td>
                            <td>{purchase.supplier?.name || purchase.contact?.name || 'N/A'}</td>
                            <td><span className={`status-badge ${purchase.status?.toLowerCase()}`}>{purchase.status || 'Pending'}</span></td>
                            <td><span className={`status-badge ${purchase.payment_status?.toLowerCase()}`}>{purchase.payment_status || 'Due'}</span></td>
                            <td>${parseFloat(purchase.final_total || 0).toFixed(2)}</td>
                            <td className="payment-due-cell">
                              <div style={{ fontSize: '0.85rem' }}>
                                <span style={{ color: '#64748b' }}>P:</span> ${parseFloat(purchase.payment_due || 0).toFixed(2)}
                              </div>
                              {purchase.return_transaction && parseFloat(purchase.return_transaction.amount_due) > 0 && (
                                <div style={{ fontSize: '0.85rem', marginTop: '2px' }}>
                                  <span style={{ color: '#ef4444' }}>R:</span> ${parseFloat(purchase.return_transaction.amount_due).toFixed(2)}
                                </div>
                              )}
                            </td>
                            <td>{resolveAddedBy(purchase)}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {/* Pagination (Simplified for now) */}
                {purchases.length > 0 && (
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem', borderTop: '1px solid #e2e8f0', background: 'white' }}>
                    <div>Showing {((currentPage - 1) * perPage) + 1} to {Math.min(currentPage * perPage, totalPurchases || purchases.length)} of {totalPurchases || purchases.length} entries</div>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>Previous</button>
                      <span style={{ display: 'flex', alignItems: 'center', padding: '0 0.5rem' }}>Page {currentPage} of {lastPage}</span>
                      <button className="pagination-btn" onClick={() => setCurrentPage(p => Math.min(lastPage, p + 1))} disabled={currentPage === lastPage}>Next</button>
                    </div>
                  </div>
                )}
              </>
            )
          )}

          {/* Placeholders for other tabs */}
          {activeTab !== 'list_purchases' && (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#64748b' }}>
              <h3>{activeTab.replace(/_/g, ' ').toUpperCase()}</h3>
              <p>Content for {activeTab.replace(/_/g, ' ')} will be displayed here.</p>
              <p>Check "List Purchases" tab for the implemented layout.</p>
              {/* Reuse table structure for visual consistency even if empty */}
              <div className="products-table-container" style={{ marginTop: '1rem' }}>
                <table className="products-table">
                  <thead>
                    <tr>
                      <th>Action</th>
                      <th>Reference No</th>
                      <th>Date</th>
                      <th>Supplier</th>
                      <th>Location</th>
                      <th>Status</th>
                      <th>Grand Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr><td colSpan="7" style={{ textAlign: 'center', padding: '2rem' }}>No data available for this section yet.</td></tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      {/* Filter Modal (Shared) */}
      {showFilters && (
        <div className="modal-overlay" onClick={() => setShowFilters(false)}>
          <div className="modal-container filter-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', width: '95%' }}>
            <div className="modal-header">
              <h2><svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" style={{ marginRight: '0.5rem', verticalAlign: 'middle' }}><path d="M10 18h4v-2h-4v2zM3 6v2h18V6H3zm3 7h12v-2H6v2z" /></svg>Filters</h2>
              <button className="modal-close-btn" onClick={() => setShowFilters(false)}>✕</button>
            </div>
            <div className="modal-content" style={{ padding: '1.5rem' }}>
              <div className="filter-row">
                <div className="filter-group"><label>Status:</label><select><option value="all">All Status</option></select></div>
                <div className="filter-group"><label>Payment Status:</label><select><option value="all">All</option></select></div>
                <div className="filter-group"><label>Supplier:</label><select><option value="all">All</option></select></div>
                <div className="filter-group"><label>Location:</label><select><option value="all">All</option></select></div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-btn close-btn" onClick={() => setShowFilters(false)}>Close</button>
              <button className="modal-btn primary-btn">Apply Filters</button>
            </div>
          </div>
        </div>
      )}
      {/* View Purchase Modal */}
      {showViewModal && selectedPurchase && (
        <div className="modal-overlay" onClick={() => setShowViewModal(false)} style={{ zIndex: 9999999 }}>
          <div onClick={e => e.stopPropagation()} style={{ 
            background: 'white', 
            borderRadius: '12px', 
            maxWidth: '1250px', 
            width: '98%', 
            maxHeight: '94vh', 
            display: 'flex', 
            flexDirection: 'column',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
            overflow: 'hidden'
          }}>
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              padding: '1.25rem 2rem', 
              borderBottom: '1px solid #e2e8f0',
              background: '#f8fafc'
            }}>
              <h2 style={{ margin: 0, fontSize: '1.4rem', fontWeight: 700, color: '#1e293b' }}>
                Purchase Details (Reference No: {selectedPurchase.ref_no})
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <span style={{ fontSize: '0.9rem', color: '#64748b', fontWeight: 500 }}>
                  Date: {selectedPurchase.transaction_date}
                </span>
                <button onClick={() => setShowViewModal(false)} style={{ background: 'none', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: '#64748b', padding: '5px' }}>✕</button>
              </div>
            </div>
            
            <div style={{ padding: '2.5rem', overflowY: 'auto', flex: 1, color: '#334155' }}>
              {/* Info Grid */}
              <div style={{ 
                display: 'grid', 
                gridTemplateColumns: '1fr 1fr 1fr', 
                gap: '2.5rem', 
                marginBottom: '2.5rem'
              }}>
                <div>
                  <h4 style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Supplier:</h4>
                  <p style={{ margin: '8px 0', fontSize: '1.1rem', fontWeight: 700, color: '#0f172a' }}>{selectedPurchase.contact?.name || selectedPurchase.supplier?.name || 'N/A'}</p>
                  <p style={{ margin: '4px 0', fontSize: '0.95rem' }}><strong>Mobile:</strong> {selectedPurchase.contact?.mobile || 'N/A'}</p>
                  <div style={{ marginTop: '12px' }}>
                    <h4 style={{ margin: '0 0 4px 0', color: '#64748b', fontSize: '0.85rem' }}>Business:</h4>
                    <p style={{ margin: '2px 0', fontSize: '0.1rem', fontWeight: 600 }}>{selectedPurchase.contact?.supplier_business_name || 'Lefty\'S Liquor'}</p>
                    <p style={{ margin: '2px 0', fontSize: '0.95rem', color: '#64748b', lineHeight: '1.4' }}>
                      {selectedPurchase.contact?.landmark || '14199 E HIGHWAY 12 ROGERS'}, <br />
                      {selectedPurchase.contact?.city || 'Little Rock'}, {selectedPurchase.contact?.state || 'AR'}, {selectedPurchase.contact?.country || 'USA'}
                    </p>
                  </div>
                </div>

                <div>
                  <h4 style={{ margin: '0 0 12px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Purchase Info:</h4>
                  <p style={{ margin: '8px 0', fontSize: '0.95rem' }}><strong>Reference No:</strong> {selectedPurchase.ref_no}</p>
                  <p style={{ margin: '8px 0', fontSize: '0.95rem' }}><strong>Date:</strong> {selectedPurchase.transaction_date}</p>
                  <p style={{ margin: '8px 0', fontSize: '0.95rem' }}><strong>Purchase Status:</strong> <span className={`status-badge ${selectedPurchase.status?.toLowerCase()}`} style={{ fontSize: '0.75rem', padding: '3px 10px' }}>{selectedPurchase.status}</span></p>
                  <p style={{ margin: '8px 0', fontSize: '0.95rem' }}><strong>Payment Status:</strong> <span className={`status-badge ${selectedPurchase.payment_status?.toLowerCase()}`} style={{ fontSize: '0.75rem', padding: '3px 10px' }}>{selectedPurchase.payment_status}</span></p>
                </div>

                <div style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '12px', border: '1px solid #e2e8f0' }}>
                  <h4 style={{ margin: '0 0 15px 0', color: '#64748b', fontSize: '0.85rem', textTransform: 'uppercase' }}>Financial Summary:</h4>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.95rem' }}>Purchase Total:</span>
                    <span style={{ fontWeight: 700 }}>$ {parseFloat(selectedPurchase.final_total || 0).toFixed(2)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                    <span style={{ fontSize: '0.95rem' }}>Amount Paid:</span>
                    <span style={{ fontWeight: 700, color: '#10b981' }}>$ { (parseFloat(selectedPurchase.final_total || 0) - parseFloat(selectedPurchase.payment_due || 0)).toFixed(2) }</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '12px', paddingTop: '12px', borderTop: '2px solid #e2e8f0' }}>
                    <span style={{ fontSize: '1rem', fontWeight: 700 }}>Payment Due:</span>
                    <span style={{ fontSize: '1.1rem', fontWeight: 900, color: '#ef4444' }}>$ {parseFloat(selectedPurchase.payment_due || 0).toFixed(2)}</span>
                  </div>
                </div>
              </div>

              {/* Products Table */}
              <div style={{ marginBottom: '3rem' }}>
                <h3 style={{ fontSize: '1.15rem', marginBottom: '1.25rem', color: '#1e293b', fontWeight: 700, borderLeft: '4px solid #3b82f6', paddingLeft: '0.75rem' }}>Items Details</h3>
                <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0', boxShadow: '0 1px 3px rgba(0,0,0,0.05)' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
                    <thead>
                      <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
                        <th style={{ padding: '14px 10px', textAlign: 'center', color: '#475569', fontWeight: 600, width: '40px' }}>#</th>
                        <th style={{ padding: '14px 10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>Product Name</th>
                        <th style={{ padding: '14px 10px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>SKU</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Purchase Quantity</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Unit Cost (Before Disc)</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Discount Percent</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Unit Cost (Before Tax)</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Subtotal (Before Tax)</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Tax</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Unit Cost Price (After Tax)</th>
                        <th style={{ padding: '14px 10px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Subtotal</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedPurchase.purchase_lines && selectedPurchase.purchase_lines.length > 0 ? (
                        selectedPurchase.purchase_lines.map((line, idx) => {
                          const qty = parseFloat(line.quantity || 0);
                          const priceBeforeDisc = parseFloat(line.pp_without_discount || line.purchase_price || 0);
                          const discPercent = parseFloat(line.discount_percent || 0);
                          const priceBeforeTax = parseFloat(line.purchase_price || 0);
                          const tax = parseFloat(line.item_tax || 0);
                          const subtotalBeforeTax = priceBeforeTax * qty;
                          const netCost = priceBeforeTax + tax;
                          const subtotal = netCost * qty;

                          return (
                            <tr key={idx} style={{ borderBottom: '1px solid #f1f5f9', background: idx % 2 === 0 ? 'white' : '#fcfdfe' }}>
                              <td style={{ padding: '12px 10px', textAlign: 'center', color: '#64748b' }}>{idx + 1}</td>
                              <td style={{ padding: '12px 10px', fontWeight: 600, color: '#0f172a' }}>{line.product?.name || `Product #${line.product_id}`}</td>
                              <td style={{ padding: '12px 10px', color: '#64748b' }}>{line.product?.sku || line.variations?.sub_sku || '--'}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 500 }}>{qty.toFixed(2)} {line.product?.unit?.short_name || 'Single'}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right' }}>$ {priceBeforeDisc.toFixed(2)}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right' }}>{discPercent.toFixed(2)} %</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right' }}>$ {priceBeforeTax.toFixed(2)}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right' }}>$ {subtotalBeforeTax.toFixed(2)}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right', color: '#64748b' }}>$ {tax.toFixed(2)}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right' }}>$ {netCost.toFixed(2)}</td>
                              <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 700, color: '#2563eb' }}>$ {subtotal.toFixed(2)}</td>
                            </tr>
                          );
                        })
                      ) : (
                        <tr><td colSpan="11" style={{ textAlign: 'center', padding: '3rem', color: '#94a3b8', background: 'white' }}>No product items found.</td></tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Payments Section */}
              <div style={{ display: 'grid', gridTemplateColumns: '1.6fr 1fr', gap: '3rem', marginBottom: '3rem' }}>
                <div>
                  <h3 style={{ fontSize: '1.1rem', marginBottom: '1.25rem', color: '#1e293b', fontWeight: 700, borderLeft: '4px solid #10b981', paddingLeft: '0.75rem' }}>Payment Info</h3>
                  <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                          <th style={{ padding: '12px', textAlign: 'center', width: '40px' }}>#</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Reference No</th>
                          <th style={{ padding: '12px', textAlign: 'right' }}>Amount</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Payment Mode</th>
                          <th style={{ padding: '12px', textAlign: 'left' }}>Payment Note</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedPurchase.payment_lines && selectedPurchase.payment_lines.length > 0 ? (
                          selectedPurchase.payment_lines.map((pmt, pIdx) => (
                            <tr key={pIdx} style={{ borderBottom: '1px solid #f1f5f9' }}>
                              <td style={{ padding: '12px', textAlign: 'center', color: '#64748b' }}>{pIdx + 1}</td>
                              <td style={{ padding: '12px' }}>{pmt.paid_on}</td>
                              <td style={{ padding: '12px' }}>{pmt.payment_ref_no || '--'}</td>
                              <td style={{ padding: '12px', textAlign: 'right', fontWeight: 700, color: '#059669' }}>$ {parseFloat(pmt.amount || 0).toFixed(2)}</td>
                              <td style={{ padding: '12px' }}>{pmt.method}</td>
                              <td style={{ padding: '12px', color: '#64748b' }}>{pmt.note || '--'}</td>
                            </tr>
                          ))
                        ) : (
                          <tr><td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>No payments found</td></tr>
                        )}
                      </tbody>
                    </table>
                  </div>

                  {/* Activities Section */}
                  <div style={{ marginTop: '3rem' }}>
                    <h3 style={{ fontSize: '1.1rem', marginBottom: '1.25rem', color: '#1e293b', fontWeight: 700, borderLeft: '4px solid #6366f1', paddingLeft: '0.75rem' }}>Activities</h3>
                    <div style={{ overflowX: 'auto', borderRadius: '10px', border: '1px solid #e2e8f0' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                        <thead>
                          <tr style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Date</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Action</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>By</th>
                            <th style={{ padding: '12px', textAlign: 'left' }}>Note</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr style={{ borderBottom: '1px solid #f1f5f9' }}>
                            <td style={{ padding: '12px' }}>{selectedPurchase.transaction_date}</td>
                            <td style={{ padding: '12px' }}><span style={{ color: '#6366f1', fontWeight: 700 }}>Added</span></td>
                            <td style={{ padding: '12px', fontWeight: 500 }}>{resolveAddedBy(selectedPurchase)}</td>
                            <td style={{ padding: '12px', color: '#64748b' }}>Original purchase entry created.</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                {/* Right Side Summary */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '2rem' }}>
                  <div style={{ background: '#f8fafc', padding: '2.5rem', borderRadius: '15px', border: '1px solid #e2e8f0', position: 'relative' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontWeight: 500 }}>Net Total Amount:</span>
                        <span style={{ fontWeight: 600 }}>$ { (parseFloat(selectedPurchase.final_total || 0) + parseFloat(selectedPurchase.discount_amount || 0) - parseFloat(selectedPurchase.shipping_charges || 0)).toFixed(2) }</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontWeight: 500 }}>Discount:</span>
                        <span style={{ color: '#ef4444', fontWeight: 600 }}>(-) $ {parseFloat(selectedPurchase.discount_amount || 0).toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontWeight: 500 }}>Purchase Tax:</span>
                        <span style={{ fontWeight: 600 }}>(+) $ 0.00</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <span style={{ color: '#64748b', fontWeight: 500 }}>Additional Shipping charges:</span>
                        <span style={{ fontWeight: 600 }}>(+) $ {parseFloat(selectedPurchase.shipping_charges || 0).toFixed(2)}</span>
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '20px', paddingTop: '20px', borderTop: '2px dashed #cbd5e1' }}>
                        <span style={{ fontSize: '1.2rem', fontWeight: 800, color: '#1e293b' }}>Purchase Total:</span>
                        <span style={{ fontSize: '1.35rem', fontWeight: 900, color: '#2563eb' }}>$ {parseFloat(selectedPurchase.final_total || 0).toFixed(2)}</span>
                      </div>
                    </div>
                  </div>

                  <div style={{ padding: '1rem' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#64748b', textTransform: 'uppercase' }}>Shipping Details:</h4>
                    <p style={{ margin: 0, fontSize: '1rem', background: '#f1f5f9', padding: '15px', borderRadius: '8px', minHeight: '60px', color: '#334155' }}>
                      {selectedPurchase.shipping_details || '--'}
                    </p>
                  </div>
                  
                  <div style={{ padding: '1rem' }}>
                    <h4 style={{ margin: '0 0 10px 0', fontSize: '0.9rem', color: '#64748b', textTransform: 'uppercase' }}>Additional Notes:</h4>
                    <p style={{ margin: 0, fontSize: '1rem', background: '#f1f5f9', padding: '15px', borderRadius: '8px', minHeight: '80px', color: '#334155' }}>
                      {selectedPurchase.additional_notes || '--'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div style={{ 
              padding: '1.5rem 2rem', 
              borderTop: '1px solid #e2e8f0', 
              display: 'flex', 
              justifyContent: 'flex-end', 
              gap: '1.5rem',
              background: '#f8fafc' 
            }}>
              <button onClick={() => setShowViewModal(false)} style={{ padding: '0.8rem 2rem', borderRadius: '8px', border: '1px solid #cbd5e1', background: 'white', color: '#475569', fontWeight: 700, cursor: 'pointer', transition: 'all 0.2s' }}>Close</button>
              <button onClick={() => window.print()} style={{ padding: '0.8rem 2.5rem', borderRadius: '8px', border: 'none', background: '#3b82f6', color: 'white', fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.75rem', boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.5)' }}>
                <i className="fa fa-print"></i> Print Details
              </button>
            </div>
          </div>
        </div>
      )}
      
      {/* Label Generator Modal */}
      <LabelGenerator
        isOpen={showLabelModal}
        onClose={() => {
          setShowLabelModal(false);
          setProductsForLabels([]);
        }}
        initialProducts={productsForLabels}
      />

      {/* Add Payment Modal */}
      {showAddPaymentModal && selectedPurchase && (
        <AddPaymentModal
          sale={selectedPurchase}
          onClose={() => {
            setShowAddPaymentModal(false);
            setSelectedPurchase(null);
          }}
          onSuccess={() => {
            fetchPurchases();
          }}
        />
      )}

      {/* View Payments Modal */}
      {showViewPaymentsModal && selectedPurchase && (
        <ViewPaymentsModal
          show={showViewPaymentsModal}
          onClose={() => setShowViewPaymentsModal(false)}
          transaction={selectedPurchase}
          onPaymentDeleted={fetchPurchases}
        />
      )}

      {showUpdateStatusModal && selectedPurchase && (
        <UpdatePurchaseStatusModal
          show={showUpdateStatusModal}
          onClose={() => setShowUpdateStatusModal(false)}
          purchase={selectedPurchase}
          onSuccess={fetchPurchases}
        />
      )}

      {showNotificationModal && selectedPurchase && (
        <PurchaseNotificationModal
          purchase={selectedPurchase}
          onClose={() => setShowNotificationModal(false)}
          onSuccess={() => {}}
        />
      )}
    </div>
  </div>
);
};

export default Purchases;
